import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ProviderProps, URLParams } from '../../common/types';
import {
	parseSearchParamsFilters,
	serializeSearchFiltersForURL
} from '../../routes/AvailabilityGuard';
import { PendingParameters } from '../../helpers/open-parameters/validatePendingParameters';

interface PendingParametersContextValue {
	pending: PendingParameters;
	setPending: (partial: Partial<PendingParameters>) => void;
	commit: () => void;
	reset: () => void;
	refreshNonce: number;
	triggerRefresh: () => void;
}

const PendingParametersContext = createContext<PendingParametersContextValue | undefined>(undefined);

function pendingFromUrlSearchParams(searchParams: URLSearchParams): PendingParameters {
	const fromUrl = parseSearchParamsFilters(searchParams);
	return { groupSize: fromUrl.groupSize, date: fromUrl.date, time: fromUrl.time };
}

function pendingMatchesUrl(pending: PendingParameters, fromUrl: PendingParameters): boolean {
	return pending.groupSize === fromUrl.groupSize
		&& pending.date === fromUrl.date
		&& pending.time === fromUrl.time;
}

export const PendingParametersProvider = ({ children }: ProviderProps) => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const { restaurantSlug } = useParams<URLParams>();

	const seedRef = useRef<PendingParameters | null>(null);
	if (seedRef.current === null) {
		seedRef.current = pendingFromUrlSearchParams(searchParams);
	}

	const [pending, setPendingState] = useState<PendingParameters>(seedRef.current);

	const pendingFromUrl = useMemo(() => pendingFromUrlSearchParams(searchParams), [searchParams]);
	// Key derived only from groupSize/date/time so unrelated query params (utm tags, etc.)
	// don't retrigger the sync below and clobber an in-progress, uncommitted picker edit.
	const urlFiltersKey = `${pendingFromUrl.groupSize ?? ''}|${pendingFromUrl.date ?? ''}|${pendingFromUrl.time ?? ''}`;

	// Keep in-memory pickers in sync when something else updates the URL (e.g. event Book Now).
	// Deliberately keyed on urlFiltersKey (not pendingFromUrl) so unrelated query params
	// (utm tags, etc.) don't retrigger this and clobber an in-progress, uncommitted edit.
	useEffect(() => {
		setPendingState((prev) => (pendingMatchesUrl(prev, pendingFromUrl) ? prev : pendingFromUrl));
	}, [urlFiltersKey]);

	const [refreshNonce, setRefreshNonce] = useState(0);
	const triggerRefresh = useCallback(() => setRefreshNonce(n => n + 1), []);

	const setPending = useCallback((partial: Partial<PendingParameters>) => {
		setPendingState(prev => ({ ...prev, ...partial }));
	}, []);

	const reset = useCallback(() => setPendingState({}), []);

	const commit = useCallback(() => {
		const serialized = serializeSearchFiltersForURL(pending);
		navigate({ pathname: `/${restaurantSlug}/explore`, search: serialized.toString() });
	}, [pending, navigate, restaurantSlug]);

	const value = useMemo<PendingParametersContextValue>(
		() => ({ pending, setPending, commit, reset, refreshNonce, triggerRefresh }),
		[pending, setPending, commit, reset, refreshNonce, triggerRefresh]
	);

	return (
		<PendingParametersContext.Provider value={value}>
			{children}
		</PendingParametersContext.Provider>
	);
};

export const usePendingParameters = (): PendingParametersContextValue => {
	const ctx = useContext(PendingParametersContext);
	if (!ctx) {
throw new Error('usePendingParameters must be used within PendingParametersProvider');
}
	return ctx;
};
